import { Box, Skeleton } from '@mui/material';
import { KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import { protoIcon } from '../common/ProtoIcon';
import { KitBanner, KitKV } from '../common/MobileKitExtras';

/**
 * Detalhe da compra (somente leitura) no shell mobile — `prototipos-mobile/compras/detalhe.html`.
 * Só apresentação: dados vêm de `pages/purchases/View.js`.
 */

const PackageIcon = protoIcon('package');
const InfoIcon = protoIcon('info');

const money = (cents) => (cents === null || cents === undefined ? '—' : (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

function fmtDay(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

function fmtIssue(value) {
    if (!value) return '';
    const [y, m, d] = String(value).split('-');
    return y && m && d ? `${d}/${m}/${y}` : '';
}

export default function MobilePurchaseDetail({ loading, data, hasValues, isAccessKey, supplierDocument }) {
    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }
    if (!data) return null;

    const reference = data.number ? `NF ${data.number}${data.series ? ` / ${data.series}` : ''}` : data.reference;
    const items = data.items || [];

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <Box sx={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere', color: data.supplier ? 'text.primary' : 'text.disabled' }}>{data.supplier || 'Sem fornecedor'}</Box>
                    <KitPill tone="success">Recebida</KitPill>
                </Box>
                <KitKV label="Data" value={fmtDay(data.date)} />
                <KitKV label="Referência" value={reference} sx={{ overflowWrap: 'anywhere' }} />
                <KitKV label="Itens" value={data.items_count} />
                <KitKV label="Qtd. total lançada" value={data.total_quantity} />
                {hasValues && data.issue_date && <KitKV label="Emissão" value={fmtIssue(data.issue_date)} />}
                {hasValues && supplierDocument && <KitKV label="CNPJ do fornecedor" value={supplierDocument} />}
                {isAccessKey && (
                    <KitKV label="Chave de acesso" value={<Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{data.reference}</Box>} />
                )}
            </KitCard>

            <SecH title="Itens" />
            <KitList>
                {items.map((item, index) => (
                    <KitItem
                        key={`${item.code}-${index}`}
                        icon={PackageIcon}
                        title={item.name}
                        sub={item.unit_cents === null || item.unit_cents === undefined ? `${item.quantity} un.` : `${item.quantity} × ${money(item.unit_cents)}`}
                        end={<Box sx={{ fontWeight: 700, fontSize: 14 }}>{money(item.total_cents)}</Box>}
                    />
                ))}
            </KitList>

            <KitCard sx={{ mt: '12px' }}>
                <KitKV big totals label="Total" value={money(data.total_cents)} sx={{ borderTop: 0, mt: 0, p: 0 }} />
            </KitCard>

            <Box sx={{ mt: '12px' }}>
                <KitBanner tone="info" icon={InfoIcon} iconSize={18}>
                    {hasValues
                        ? 'Itens não lançados da nota não são exibidos. O valor unitário é o da nota (vUnCom) e o total do item é valor unitário × quantidade lançada.'
                        : 'Esta compra foi lançada antes do registro de valores: custos e valores da nota não foram guardados e itens não lançados não são exibidos.'}
                </KitBanner>
            </Box>
        </Box>
    );
}
